import EventEmitter from "eventemitter3";
import { toast } from "react-toastify";

import RenderWorker from "../worker/render.worker?worker";
import {
  type CanvasPixel,
  CanvasRenderer,
  type CanvasRole,
  type RendererCaptureArea,
  type RendererEvents,
} from "./canvasRenderer";
import { type ExtractMethods } from "./utils";

const hasWorkerSupport =
  typeof Worker !== "undefined" && !localStorage.getItem("no_workers");

export abstract class Renderer
  extends EventEmitter<RendererEvents>
  implements ICanvasRenderer
{
  hasWorker: boolean;

  constructor(hasWorker: boolean) {
    super();
    this.hasWorker = hasWorker;
  }

  /**
   * Get the renderer that is available to the client
   * @returns
   */
  static create(): Renderer {
    if (hasWorkerSupport) {
      return new WorkerRenderer();
    } else {
      return new LocalRenderer();
    }
  }

  abstract usePixels(pixels: CanvasPixel[], replace?: boolean): void;
  abstract usePixel(pixel: CanvasPixel): void;
  abstract draw(): void;
  abstract setSize(width: number, height: number): void;
  abstract useCanvas(canvas: HTMLCanvasElement, role: CanvasRole): void;
  abstract removeCanvas(role: CanvasRole): void;
  abstract startRender(): void;
  abstract stopRender(): void;
  abstract capture(area?: RendererCaptureArea): Promise<Blob>;
}

type ICanvasRenderer = Omit<
  ExtractMethods<CanvasRenderer>,
  "useCanvas" | keyof ExtractMethods<EventEmitter>
> & {
  useCanvas: (canvas: HTMLCanvasElement, role: CanvasRole) => void;
};

class WorkerRenderer extends Renderer implements ICanvasRenderer {
  private worker: Worker;

  private captureRequestId = 0;
  private captureRequests = new Map<
    number,
    { resolve: (blob: Blob) => void; reject: (error: Error) => void }
  >();

  constructor() {
    super(true);
    this.worker = new RenderWorker();
    this.worker.addEventListener("message", (req) => {
      if (req.data.type === "ready") {
        this.emit("ready");
      } else if (req.data.type === "snapshot") {
        const request = this.captureRequests.get(req.data.requestId);
        if (!request) return;

        this.captureRequests.delete(req.data.requestId);
        if (req.data.blob instanceof Blob) request.resolve(req.data.blob);
        else request.reject(new Error(req.data.error || "Snapshot failed"));
      }
    });
    this.worker.addEventListener("error", (err) => {
      console.log(JSON.stringify(err));
      console.error("[WorkerRenderer]", err.message, err.filename, err.lineno);
    });
  }

  destroy(): void {
    console.warn("[WorkerRender#destroy] Destroying worker");
    this.worker.terminate();
  }

  useCanvas(canvas: HTMLCanvasElement, role: CanvasRole): void {
    try {
      const offscreen = canvas.transferControlToOffscreen();
      this.worker.postMessage({ type: "canvas", role, canvas: offscreen }, [
        offscreen,
      ]);
    } catch (e) {
      console.error("Failed to capture canvas", canvas, role, e);
    }
  }

  removeCanvas(role: CanvasRole): void {
    this.worker.postMessage({ type: "remove-canvas", role });
  }

  usePixels(pixels: CanvasPixel[], replace: boolean): void {
    this.worker.postMessage({
      type: "pixels",
      replace,
      pixels: pixels
        .map((pixel) => pixel.x + "," + pixel.y + "," + pixel.hex)
        .join(";"),
    });
  }

  usePixel({ x, y, hex }: CanvasPixel): void {
    this.worker.postMessage({
      type: "pixel",
      pixel: x + "," + y + "," + (hex || "null"),
    });
  }

  startDrawLoop(): void {
    throw new Error("Method not implemented.");
  }

  startRender(): void {
    this.worker.postMessage({ type: "startRender" });
  }

  stopRender(): void {
    this.worker.postMessage({ type: "stopRender" });
  }

  draw(): void {
    this.worker.postMessage({ type: "draw" });
  }

  setSize(width: number, height: number): void {
    this.worker.postMessage({ type: "size", width, height });
  }

  capture(area?: RendererCaptureArea): Promise<Blob> {
    const requestId = ++this.captureRequestId;
    return new Promise((resolve, reject) => {
      this.captureRequests.set(requestId, { resolve, reject });
      this.worker.postMessage({ type: "snapshot", requestId, area });
    });
  }
}

class LocalRenderer extends Renderer implements ICanvasRenderer {
  reference: CanvasRenderer;

  constructor() {
    super(false);

    toast.error(
      "Your browser doesn't support WebWorkers, this will cause performance issues",
    );

    this.reference = new CanvasRenderer();
    this.reference.on("ready", () => this.emit("ready"));
  }

  useCanvas(canvas: HTMLCanvasElement, role: CanvasRole): void {
    this.reference.useCanvas(canvas, role);
  }
  removeCanvas(role: CanvasRole) {
    this.reference.removeCanvas(role);
  }
  usePixels(pixels: CanvasPixel[], replace: boolean): void {
    this.reference.usePixels(pixels, replace);
  }
  usePixel(pixel: CanvasPixel): void {
    this.reference.usePixel(pixel);
  }
  startRender(): void {
    this.reference.startRender();
  }
  stopRender(): void {
    this.reference.stopRender();
  }
  draw(): void {
    this.reference.draw();
  }
  setSize(width: number, height: number): void {
    this.reference.setSize(width, height);
  }
  capture(area?: RendererCaptureArea): Promise<Blob> {
    return this.reference.capture(area);
  }
}
